import type { BookShelfConfig } from "../../../../types/floorplannerTypes";
import {
  BOOK_SHELF_BACK_THICKNESS_M,
  BOOK_SHELF_BOARD_THICKNESS_M,
  BOOK_SHELF_SIDE_THICKNESS_M,
  normalizeBookShelfConfig,
} from "./bookShelfConfig";

export type BookShelfShelfBoard = {
  id: string;
  /** World Z of the board center. */
  centerZ: number;
  /** Clear opening above this board (0 for the top board). */
  openingAbove: number;
};

export type ResolvedBookShelfLayout = {
  config: BookShelfConfig;
  /** Plan footprint width (X) and depth (Y). */
  bounds: {
    width: number;
    depth: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  sideThickness: number;
  boardThickness: number;
  backThickness: number;
  /** Interior clear width between side panels. */
  innerWidth: number;
  /** Shelf depth excluding back panel thickness when a back panel is present. */
  shelfDepth: number;
  /** Y center of shelf boards (shifted forward when a back panel is present). */
  shelfCenterY: number;
  shelves: BookShelfShelfBoard[];
};

/**
 * Resolve plan bounds and vertical shelf board placements for a bookshelf config.
 */
export function resolveBookShelfLayout(value: unknown): ResolvedBookShelfLayout {
  const config = normalizeBookShelfConfig(value);
  const { width, depth, shelfCount, shelfSpacing, hasFrame, hasBackPanel } =
    config;
  const sideThickness = hasFrame ? BOOK_SHELF_SIDE_THICKNESS_M : 0;
  const boardThickness = BOOK_SHELF_BOARD_THICKNESS_M;
  const backThickness = hasBackPanel ? BOOK_SHELF_BACK_THICKNESS_M : 0;
  const innerWidth = Math.max(width - 2 * sideThickness, boardThickness);
  const shelfDepth = Math.max(depth - backThickness, boardThickness);
  const shelfCenterY = hasBackPanel ? -backThickness / 2 : 0;

  const shelves: BookShelfShelfBoard[] = [];
  for (let index = 0; index < shelfCount; index += 1) {
    const centerZ =
      boardThickness / 2 + index * (boardThickness + shelfSpacing);
    shelves.push({
      id: `shelf-${index}`,
      centerZ,
      openingAbove: index < shelfCount - 1 ? shelfSpacing : 0,
    });
  }

  return {
    config,
    bounds: {
      width,
      depth,
      minX: -width / 2,
      maxX: width / 2,
      minY: -depth / 2,
      maxY: depth / 2,
    },
    sideThickness,
    boardThickness,
    backThickness,
    innerWidth,
    shelfDepth,
    shelfCenterY,
    shelves,
  };
}
